import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import S3Image from 'Components/S3Image'
import { SchoolIcon } from 'Assets/SVGs/school'
import LocationIcon from '@mui/icons-material/LocationOnOutlined'
import PhoneIcon from '@mui/icons-material/PhoneOutlined'
import WebIcon from '@mui/icons-material/LanguageOutlined'
import EmailIcon from '@mui/icons-material/Email'
import BusinessIcon from '@mui/icons-material/Business'
import { useEffect, useState } from 'react'
import { sendGet } from 'hooks/use-fetch'
import { type location } from 'core/types'

interface ProviderData {
  id: number
  name: string
  eemCode: string
  website: string
  iconID?: number
  contactEmail: string
  contactPhone: string
  locationID?: number
  imageKey: string | null
  location: location
}

interface ProviderDetailsProps {
  enrollmentID: number
  // the building (facility) the enrollment is at, if one has been assigned
  buildingName?: string | null
}

// the provider responsible for a caregiver's enrollment - loaded through the caregiver portal so access is
// checked against the enrollment's child rather than opening the staff ServiceProviderDetails endpoint
export function ProviderDetails (props: ProviderDetailsProps): JSX.Element {
  const [data, setData] = useState<ProviderData | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    const loadDetails = async (): Promise<void> => {
      const { response, success } = await sendGet(`/CaregiverPortal/GetEnrollmentProvider/${props.enrollmentID}`, {})
      if (success) {
        setData(response)
      }
      setLoaded(true)
    }
    void loadDetails()
  }, [props.enrollmentID])

  if (!loaded) {
    return <CircularProgress />
  } else if (data == null) {
    return <Box>There was an error loading the provider details.</Box>
  } else {
    const hasValueCity = data.location.city != null && data.location.city !== ''
    const hasValueState = data.location.state != null && data.location.state !== ''
    const hasValueAddress = data.location.address != null && data.location.address !== ''
    const hasValueZipCode = data.location.zipCode != null && data.location.zipCode !== ''

    return <Box className='CardDisplay'>
      <Typography variant='h6' sx={{ fontWeight: 'bold' }}>Provider Details</Typography>

      <Stack direction='column' spacing={1}>
        <Stack direction='row' spacing={1}>
          {data.imageKey != null
            ? <S3Image style={{ width: '30px', height: '30px' }} imageKey={data.imageKey} />
            : <SchoolIcon size={24} />}
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>{data.name}</Typography>
        </Stack>

        {(props.buildingName ?? '') !== '' && <Stack direction='row' spacing={1} data-testid='providerBuilding'>
          <BusinessIcon />
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>{props.buildingName}</Typography>
        </Stack>
        }

        {data.contactPhone !== '' && <Stack direction='row' spacing={1}>
          <PhoneIcon />
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>{data.contactPhone}</Typography>
        </Stack>
        }

        {data.contactEmail !== '' && <Stack direction='row' spacing={1}>
          <EmailIcon />
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}><a href={`mailto:${data.contactEmail}`}>{data.contactEmail}</a></Typography>
        </Stack>
        }

        {data.website !== '' && <Stack direction='row' spacing={1}>
          <WebIcon />
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}><a href={data.website} target="_blank">{data.website}</a></Typography>
        </Stack>
        }
        <Stack direction='row' spacing={1}>
          <LocationIcon />
          <Stack direction='column'>
            {hasValueAddress && (
              <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
                {data.location.apartment === '' ? data.location.address : `${data.location.address} - ${data.location.apartment}`}
              </Typography>
            )}
            {(hasValueCity || hasValueState || hasValueZipCode) &&
              <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
                {data.location.city}, {data.location.state} {data.location.zipCode}
              </Typography>
            }
          </Stack>
        </Stack>
      </Stack>
    </Box>
  }
}
